import { HashtagIcon, XIcon } from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

/**
 * A Slack channel where someone @mentions the agent, and the thread it
 * answers in, so the channel itself stays tidy.
 */
export default function SlackThread({
  brand,
  channel,
  asker,
  question,
  answer,
}: {
  brand: DemoBrand;
  channel: string;
  asker: { name: string; initials: string };
  /** The question, after the @mention. */
  question: string;
  answer: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[32rem] pb-2 sm:pb-20'>
      <div className='overflow-hidden rounded-[18px] bg-white text-start text-[13.5px] leading-[1.45] text-[#1d1c1d] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 dark:bg-[#1a1d21] dark:text-[#d1d2d3]'>
        <div className='flex items-center gap-1.5 border-b border-black/[0.08] px-4 py-3 font-bold dark:border-white/[0.08] dark:text-white'>
          <HashtagIcon className='h-4 w-4' />
          <span dir='ltr'>{channel}</span>
        </div>
        <div className='space-y-4 px-4 py-4'>
          <div className='flex gap-2.5 opacity-60'>
            <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#1264a3] text-[13px] font-bold text-white'>
              JM
            </span>
            <div className='min-w-0'>
              <p className='text-[14px] font-bold dark:text-white'>Jonas M.</p>
              <p>{t('Welcome to the team, everyone! 👋')}</p>
            </div>
          </div>
          <div className='flex gap-2.5'>
            <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#7c6f64] text-[13px] font-bold text-white'>
              {asker.initials}
            </span>
            <div className='min-w-0'>
              <p className='text-[14px] font-bold dark:text-white'>
                {asker.name}
              </p>
              <p>
                <span className='rounded bg-[#1d9bd1]/15 px-0.5 font-medium text-[#1264a3] dark:text-[#1d9bd1]'>
                  @{brand.name}
                </span>{' '}
                {question}
              </p>
              <p className='mt-1.5 flex items-center gap-1.5 text-[12.5px] font-bold text-[#1264a3] dark:text-[#1d9bd1]'>
                <span
                  className={clsxm(
                    'flex h-5 w-5 items-center justify-center rounded text-[9px] font-bold text-white',
                    brand.bg,
                  )}
                >
                  {brand.initial}
                </span>
                {t('1 reply')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 overflow-hidden rounded-[18px] bg-white text-start text-[13.5px] leading-[1.45] text-[#1d1c1d] shadow-[0_30px_70px_-25px_rgba(0,0,0,0.75)] ring-1 ring-black/10 dark:bg-[#222529] dark:text-[#d1d2d3] dark:ring-white/10 sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[19rem]',
        )}
        style={delay(450)}
      >
        <div className='flex items-center justify-between border-b border-black/[0.08] px-4 py-2.5 dark:border-white/[0.08]'>
          <span className='text-[14px] font-bold dark:text-white'>
            {t('Thread')}
          </span>
          <XIcon className='h-4 w-4 text-[#616061]' />
        </div>
        <div className='flex gap-2.5 px-4 py-3.5'>
          <span
            className={clsxm(
              'flex h-9 w-9 flex-none items-center justify-center rounded-lg text-[13px] font-bold text-white',
              brand.bg,
            )}
          >
            {brand.initial}
          </span>
          <div className='min-w-0'>
            <p className='flex items-baseline gap-1.5'>
              <span className='text-[14px] font-bold dark:text-white'>
                {brand.name}
              </span>
              <span className='rounded bg-[#1d1c1d]/[0.08] px-1 py-px text-[9.5px] font-bold uppercase tracking-wide text-[#616061] dark:bg-white/10 dark:text-[#ababad]'>
                {t('App')}
              </span>
            </p>
            <p className='mt-0.5'>{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
